import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  FileText, Play, Save, Download, Trash2, RefreshCw, Database,
  Table, Filter, SortAsc, Layers, Plus, FolderOpen
} from 'lucide-react';

const ReportBuilderPage = () => {
  const [sources, setSources] = useState({});
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);

  // Builder state
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filters, setFilters] = useState({});
  const [groupBy, setGroupBy] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // Results
  const [results, setResults] = useState(null);

  // Save dialog
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const fetchSources = useCallback(async () => {
    try {
      setLoading(true);
      const [srcRes, tplRes] = await Promise.all([
        api.get('/report-builder/sources'),
        api.get('/report-builder/templates')
      ]);
      setSources(srcRes.data);
      setTemplates(tplRes.data);
    } catch {
      toast.error('Eroare la incarcarea surselor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  const handleSourceChange = (source) => {
    setSelectedSource(source);
    setSelectedColumns([]);
    setFilters({});
    setGroupBy('');
    setSortBy('');
    setResults(null);
  };

  const toggleColumn = (col) => {
    setSelectedColumns(prev =>
      prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]
    );
  };

  const selectAllColumns = () => {
    if (!selectedSource || !sources[selectedSource]) return;
    const allCols = Object.keys(sources[selectedSource].columns);
    setSelectedColumns(allCols);
  };

  const handleExecute = async () => {
    if (!selectedSource || selectedColumns.length === 0) {
      toast.error('Selectati o sursa si cel putin o coloana');
      return;
    }
    try {
      setExecuting(true);
      const res = await api.post('/report-builder/execute', {
        source: selectedSource,
        columns: selectedColumns,
        filters,
        group_by: groupBy || null,
        sort_by: sortBy || null,
        sort_order: sortOrder,
        limit: 500
      });
      setResults(res.data);
      toast.success(`Raport generat: ${res.data.total_rows} randuri`);
    } catch {
      toast.error('Eroare la generarea raportului');
    } finally {
      setExecuting(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      await api.post('/report-builder/templates', {
        name: templateName,
        source: selectedSource,
        columns: selectedColumns,
        filters,
        group_by: groupBy || null,
        sort_by: sortBy || null,
        sort_order: sortOrder
      });
      toast.success('Template salvat');
      setShowSaveDialog(false);
      setTemplateName('');
      fetchSources();
    } catch { toast.error('Eroare la salvare template'); }
  };

  const handleLoadTemplate = async (tpl) => {
    setSelectedSource(tpl.source);
    setSelectedColumns(tpl.columns);
    setFilters(tpl.filters || {});
    setGroupBy(tpl.group_by || '');
    setSortBy(tpl.sort_by || '');
    setSortOrder(tpl.sort_order || 'asc');
    // Auto-execute
    try {
      setExecuting(true);
      const res = await api.post(`/report-builder/templates/${tpl.id}/execute`);
      setResults(res.data);
      toast.success(`Raport generat: ${res.data.total_rows} randuri`);
    } catch { toast.error('Eroare la executia template'); } finally { setExecuting(false); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Stergeti template-ul?')) return;
    try {
      await api.delete(`/report-builder/templates/${id}`);
      toast.success('Template sters');
      fetchSources();
    } catch { toast.error('Eroare la stergere'); }
  };

  const exportCSV = () => {
    if (!results || !results.data.length) return;
    const labels = results.column_labels;
    const header = results.columns.map(c => labels[c] || c);
    const rows = results.data.map(row => results.columns.map(c => {
      const val = row[c];
      if (val === null || val === undefined) return '';
      return String(val).includes(',') ? `"${val}"` : String(val);
    }));
    const csv = [header.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `raport_${selectedSource}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const currentSourceColumns = selectedSource && sources[selectedSource] ? sources[selectedSource].columns : {};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="report-builder-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Report Builder</h1>
          <p className="text-slate-500 mt-1">Construieste rapoarte personalizate din orice sursa de date</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Left Panel: Builder */}
        <div className="lg:col-span-4 space-y-4">
          {/* Source Selector */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Database className="w-4 h-4" /> Sursa Date</CardTitle></CardHeader>
            <CardContent>
              <Select value={selectedSource} onValueChange={handleSourceChange}>
                <SelectTrigger data-testid="source-select"><SelectValue placeholder="Selecteaza sursa" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(sources).map(([key, src]) => (
                    <SelectItem key={key} value={key}>{src.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Column Selector */}
          {selectedSource && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><Table className="w-4 h-4" /> Coloane</CardTitle>
                  <Button size="sm" variant="ghost" className="text-xs" onClick={selectAllColumns}>Selecteaza toate</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {Object.entries(currentSourceColumns).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 py-1 px-2 rounded">
                      <Checkbox
                        checked={selectedColumns.includes(key)}
                        onCheckedChange={() => toggleColumn(key)}
                        data-testid={`col-${key}`}
                      />
                      <span className="text-sm text-slate-700">{label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">{selectedColumns.length} coloane selectate</p>
              </CardContent>
            </Card>
          )}

          {/* Filters */}
          {selectedSource && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Filter className="w-4 h-4" /> Filtre</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Data de la</Label>
                  <Input type="date" value={filters.date_from || ''} onChange={e => setFilters(p => ({ ...p, date_from: e.target.value }))} data-testid="filter-date-from" />
                </div>
                <div>
                  <Label className="text-xs">Data pana la</Label>
                  <Input type="date" value={filters.date_to || ''} onChange={e => setFilters(p => ({ ...p, date_to: e.target.value }))} data-testid="filter-date-to" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sort & Group */}
          {selectedSource && selectedColumns.length > 0 && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><SortAsc className="w-4 h-4" /> Sortare & Grupare</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Sorteaza dupa</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger><SelectValue placeholder="Selecteaza" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at">Data creare</SelectItem>
                      {selectedColumns.map(c => (
                        <SelectItem key={c} value={c}>{currentSourceColumns[c] || c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Ordine</Label>
                  <Select value={sortOrder} onValueChange={setSortOrder}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">Crescator</SelectItem>
                      <SelectItem value="desc">Descrescator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Grupeaza dupa</Label>
                  <Select value={groupBy || "none"} onValueChange={(v) => setGroupBy(v === "none" ? "" : v)}>
                    <SelectTrigger><SelectValue placeholder="Fara grupare" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Fara grupare</SelectItem>
                      {selectedColumns.map(c => (
                        <SelectItem key={c} value={c}>{currentSourceColumns[c] || c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          {selectedSource && selectedColumns.length > 0 && (
            <div className="flex gap-2">
              <Button className="flex-1" onClick={handleExecute} disabled={executing} data-testid="execute-report-btn">
                {executing ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                Genereaza Raport
              </Button>
              <Button variant="outline" onClick={() => setShowSaveDialog(true)} data-testid="save-template-btn">
                <Save className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Saved Templates */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Template-uri Salvate</CardTitle>
                {templates.length < 15 && (
                  <Button variant="outline" size="sm" className="text-xs" onClick={async () => {
                    try {
                      const res = await api.post('/report-builder/templates/seed');
                      toast.success(res.data.message);
                      fetchSources();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la generare'); }
                  }} data-testid="seed-templates-btn">
                    <Plus className="w-3 h-3 mr-1" /> Genereaza Default
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-center py-6 text-slate-400">
                  <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Niciun template salvat</p>
                  <p className="text-xs mt-1">Apasati "Genereaza Default" pentru template-uri predefinite</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.map(tpl => (
                    <div key={tpl.id} className="flex items-center gap-2 p-2 rounded border border-slate-100 hover:bg-slate-50">
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleLoadTemplate(tpl)}>
                        <p className="text-sm font-medium text-slate-900 truncate">{tpl.name}</p>
                        <p className="text-xs text-slate-400">{sources[tpl.source]?.label || tpl.source} | {tpl.columns?.length} col.</p>
                      </div>
                      <Button size="icon" variant="ghost" className="text-red-500 h-7 w-7" onClick={() => handleDeleteTemplate(tpl.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel: Results */}
        <div className="lg:col-span-8">
          {!results ? (
            <Card className="h-full">
              <CardContent className="flex flex-col items-center justify-center h-96 text-slate-400">
                <FileText className="w-16 h-16 mb-4" />
                <p className="text-lg font-medium">Selectati sursa si coloanele</p>
                <p className="text-sm mt-1">Apoi apasati "Genereaza Raport" pentru a vedea rezultatele</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{results.source_label}</CardTitle>
                    <p className="text-sm text-slate-500">{results.total_rows} randuri</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-results-csv">
                    <Download className="w-4 h-4 mr-1" /> Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {results.grouped && (
                  <div className="mb-4 flex flex-wrap gap-2">
                    {Object.entries(results.grouped).map(([key, val]) => (
                      <Badge key={key} className="bg-blue-50 text-blue-700">
                        {key}: {val.count}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 text-xs text-slate-500">#</th>
                        {results.columns.map(col => (
                          <th key={col} className="text-left py-2 px-3 text-xs font-semibold text-slate-700">
                            {results.column_labels[col] || col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.data.map((row, i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-2 px-3 text-xs text-slate-400">{i + 1}</td>
                          {results.columns.map(col => (
                            <td key={col} className="py-2 px-3 text-slate-900 max-w-[200px] truncate">
                              {row[col] === true ? 'Da' : row[col] === false ? 'Nu' : (row[col] ?? '-')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Save Template Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Salveaza Template Raport</DialogTitle></DialogHeader>
          <div>
            <Label>Denumire template</Label>
            <Input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="ex: Raport echipamente lunar" data-testid="template-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Anuleaza</Button>
            <Button onClick={handleSaveTemplate} disabled={!templateName.trim()} data-testid="save-template-confirm-btn">Salveaza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportBuilderPage;
